import { supabase } from '../utils/supabase';
import type { OptimizationStrategy, OptimizedScenario, ImprovementOption, ProgressCallback } from '../utils/scenario-optimizer';
import {
  calculateAllPossibleImprovements,
  generateOptimizedScenario,
} from '../utils/scenario-optimizer';
import { fetchApplicableEquipmentsByCategory } from './work-scenarios-service';
import { enrichDPEWithEstimates } from '../utils/dpe-fallback-estimator';
import { normalizeDPEData } from '../utils/dpe-data-normalizer';
import { calculateProjectedDPELabel, calculateTotalGESWithImprovements } from '../utils/work-scenarios-calculator';
import { normalizeAnswers } from '../utils/work-recommendations-filter';

const OPTIMIZATION_TIMEOUT_MS = 120000; // 120 seconds timeout (increased for large equipment sets)

export async function generateOptimizationForDPE(
  dpeId: string,
  strategy: OptimizationStrategy,
  onProgress?: ProgressCallback
): Promise<OptimizedScenario> {
  console.log('[ScenarioOptimizer] Starting optimization', { dpeId, strategy });

  const startTime = Date.now();

  // Create timeout promise
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error('L\'optimisation a pris trop de temps. Veuillez réessayer avec moins d\'équipements ou contacter le support.'));
    }, OPTIMIZATION_TIMEOUT_MS);
  });

  // Wrap the entire optimization in a race against the timeout
  return Promise.race([
    timeoutPromise,
    (async () => {

  if (onProgress) {
    onProgress(5, 'Chargement des données DPE...');
  }

  const { data: rawDpeData, error: dpeError } = await supabase
    .from('dpe_data')
    .select('*')
    .eq('id', dpeId)
    .maybeSingle();

  if (dpeError || !rawDpeData) {
    console.error('[ScenarioOptimizer] DPE data not found', { dpeId, error: dpeError });
    throw new Error('DPE introuvable. Veuillez vérifier que l\'analyse DPE a bien été effectuée.');
  }

  console.log('[ScenarioOptimizer] Raw DPE loaded', {
    surface: rawDpeData.surface_habitable_logement,
    classe: rawDpeData.etiquette_dpe,
    hasHeatingSystem: !!rawDpeData.heating_system,
  });

  let normalizedDPE;
  try {
    normalizedDPE = normalizeDPEData(rawDpeData);
    console.log('[ScenarioOptimizer] DPE normalized successfully', {
      living_area: normalizedDPE.living_area,
      energy_class: normalizedDPE.energy_class,
      primary_energy_consumption: normalizedDPE.primary_energy_consumption,
      heating_energy: normalizedDPE.heating_energy,
      heating_type: normalizedDPE.heating_type,
      has_flat_roof: normalizedDPE.has_flat_roof,
      has_attic: normalizedDPE.has_attic,
    });
  } catch (normalizationError) {
    console.error('[ScenarioOptimizer] Normalization failed', normalizationError);
    throw new Error(`Erreur lors de la normalisation des données DPE : ${normalizationError instanceof Error ? normalizationError.message : 'Erreur inconnue'}`);
  }

  if (!normalizedDPE.living_area || normalizedDPE.living_area <= 0) {
    console.error('[ScenarioOptimizer] Invalid living area', { living_area: normalizedDPE.living_area });
    throw new Error('Surface habitable invalide. Impossible de calculer les scénarios d\'optimisation.');
  }

  if (!normalizedDPE.primary_energy_consumption || normalizedDPE.primary_energy_consumption <= 0) {
    console.error('[ScenarioOptimizer] Invalid energy consumption', { primary_energy_consumption: normalizedDPE.primary_energy_consumption });
    throw new Error('Consommation énergétique invalide. Les données DPE sont incomplètes.');
  }

  let enrichedDPE;
  try {
    enrichedDPE = enrichDPEWithEstimates(normalizedDPE as any);
    console.log('[ScenarioOptimizer] DPE enriched successfully', {
      has_estimated_data: enrichedDPE.has_estimated_data,
      besoin_chauffage: enrichedDPE.besoin_chauffage,
      besoin_ecs: enrichedDPE.besoin_ecs,
    });
  } catch (enrichmentError) {
    console.error('[ScenarioOptimizer] Enrichment failed', enrichmentError);
    throw new Error(`Erreur lors de l'enrichissement des données DPE : ${enrichmentError instanceof Error ? enrichmentError.message : 'Erreur inconnue'}`);
  }

  const { data: sessionData } = await supabase
    .from('questionnaire_sessions')
    .select('answers_json')
    .eq('dpe_id', dpeId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  // answers_json est un tableau d'objets { question_id, selected_option_id, ... }
  // Il faut le normaliser en objet plat { question_id: selected_option_id }
  // pour que les calculateurs puissent accéder aux réponses via answersMap['question_id']
  const rawAnswers = sessionData?.answers_json || [];
  const userAnswers = normalizeAnswers(rawAnswers);
  console.log('[ScenarioOptimizer] User answers loaded', {
    hasAnswers: Object.keys(userAnswers).length > 0,
    answerCount: Object.keys(userAnswers).length,
    hasPourcentageMurs: !!userAnswers['pourcentage_murs_isoles_8'],
    pourcentageMursValue: userAnswers['pourcentage_murs_isoles_8'],
  });

  if (onProgress) {
    onProgress(8, 'Chargement des équipements disponibles...');
  }

  let equipments;
  try {
    const result = await fetchApplicableEquipmentsByCategory(dpeId);
    equipments = result.equipments;
    console.log('[ScenarioOptimizer] Equipments loaded', {
      totalCount: Object.values(equipments).flat().length,
      categories: Object.keys(equipments),
      byCategoryCount: Object.entries(equipments).reduce((acc, [cat, items]) => ({
        ...acc,
        [cat]: items.length
      }), {}),
    });
  } catch (equipmentError) {
    console.error('[ScenarioOptimizer] Failed to load equipments', equipmentError);
    throw new Error('Impossible de charger les équipements disponibles. Veuillez réessayer.');
  }

  if (Object.values(equipments).flat().length === 0) {
    console.error('[ScenarioOptimizer] No equipments available');
    throw new Error('Aucun équipement disponible pour générer des améliorations. Veuillez contacter le support.');
  }

  let allPossibleImprovements;
  try {
    if (onProgress) {
      try {
        onProgress(10, 'Analyse des équipements disponibles...');
      } catch (progressError) {
        console.error('[ScenarioOptimizer] Progress callback failed at 10%:', progressError);
      }
    }

    allPossibleImprovements = await calculateAllPossibleImprovements(
      enrichedDPE as any,
      equipments,
      userAnswers,
      (progress, message) => {
        if (onProgress) {
          try {
            // Map 0-100 to 10-90 range (leaving 0-10 for data loading and 90-100 for final optimization)
            const mappedProgress = 10 + Math.round(progress * 0.8);
            onProgress(mappedProgress, message);
          } catch (progressError) {
            console.error('[ScenarioOptimizer] Progress callback failed:', progressError);
          }
        }
      }
    );
    console.log('[ScenarioOptimizer] Possible improvements calculated', {
      count: allPossibleImprovements.length,
      categories: [...new Set(allPossibleImprovements.map(i => i.category))],
    });
    console.log('[DEBUG improvements]', {
  count: allPossibleImprovements.length,
  categories: allPossibleImprovements.map(i => i.category),
  firstImprovement: allPossibleImprovements[0],
});
  } catch (calculationError) {
    console.error('[ScenarioOptimizer] Failed to calculate improvements', calculationError);
    throw new Error(`Erreur lors du calcul des améliorations : ${calculationError instanceof Error ? calculationError.message : 'Erreur inconnue'}`);
  }

  if (allPossibleImprovements.length === 0) {
    console.warn('[ScenarioOptimizer] No improvements found', {
      living_area: enrichedDPE.living_area,
      energy_class: enrichedDPE.energy_class,
      equipmentCount: Object.values(equipments).flat().length,
    });
    throw new Error('Aucune amélioration possible trouvée pour ce DPE. Vérifiez que les données sont complètes et que le questionnaire a été rempli correctement.');
  }

  let optimizedScenario;
  try {
    if (onProgress) {
      try {
        onProgress(90, 'Génération du scénario optimal...');
      } catch (progressError) {
        console.error('[ScenarioOptimizer] Progress callback failed at 90%:', progressError);
      }
    }

    console.log('[ScenarioOptimizer] Generating optimized scenario...');
    optimizedScenario = await generateOptimizedScenario(
      enrichedDPE as any,
      allPossibleImprovements,
      strategy
    );
    console.log('[ScenarioOptimizer] Optimization generated successfully', {
      strategy,
      workCount: optimizedScenario.improvements.length,
      totalInvestment: optimizedScenario.totalInvestment,
      labelGain: optimizedScenario.summary.labelGain,
      currentLabel: optimizedScenario.currentLabel,
      projectedLabel: optimizedScenario.projectedLabel,
      gesGainPercentage: optimizedScenario.summary.gesGainPercentage,
    });

    if (onProgress) {
      try {
        onProgress(100, 'Scénario optimisé généré avec succès');
      } catch (progressError) {
        console.error('[ScenarioOptimizer] Progress callback failed at 100%:', progressError);
      }
    }
  } catch (optimizationError) {
    console.error('[ScenarioOptimizer] Failed to generate scenario', optimizationError);
    throw new Error(`Erreur lors de la génération du scénario optimisé : ${optimizationError instanceof Error ? optimizationError.message : 'Erreur inconnue'}`);
  }

  if (!optimizedScenario.improvements || optimizedScenario.improvements.length === 0) {
    console.warn('[ScenarioOptimizer] Generated scenario has no improvements');
    throw new Error('Le scénario généré ne contient aucune amélioration. Cela peut être dû à des contraintes trop restrictives ou à des données DPE incomplètes.');
  }

  const elapsedTime = Date.now() - startTime;
  console.log(`[ScenarioOptimizer] Optimization completed in ${elapsedTime}ms`);

  return optimizedScenario;
    })()
  ]);
}

export async function applyOptimizationToScenario(
  scenarioId: string,
  optimizedScenario: OptimizedScenario
): Promise<void> {
  const { data: scenario, error: scenarioError } = await supabase
    .from('work_scenarios')
    .select('dpe_id')
    .eq('id', scenarioId)
    .maybeSingle();

  if (scenarioError || !scenario) {
    throw new Error('Scenario not found');
  }

  const { data: rawDpeData, error: dpeError } = await supabase
    .from('dpe_data')
    .select('*')
    .eq('id', scenario.dpe_id)
    .maybeSingle();

  if (dpeError || !rawDpeData) {
    throw new Error('DPE data not found');
  }

  const { data: sessionData } = await supabase
    .from('questionnaire_sessions')
    .select('answers_json')
    .eq('dpe_id', scenario.dpe_id)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  const rawAnswers2 = sessionData?.answers_json || [];
  const userAnswers = normalizeAnswers(rawAnswers2);

  const normalizedDPE = normalizeDPEData(rawDpeData);
  console.log('[DEBUG normalizedDPE]', {
  living_area: normalizedDPE.living_area,
  primary_energy_consumption: normalizedDPE.primary_energy_consumption,
  heating_energy: normalizedDPE.heating_energy,
  heating_type: normalizedDPE.heating_type,
  climate_zone: normalizedDPE.climate_zone,
  besoin_chauffage: normalizedDPE.besoin_chauffage,
  besoin_ecs: normalizedDPE.besoin_ecs,
  deperditions_murs: normalizedDPE.deperditions_murs,
});
  
  const enrichedDPE = enrichDPEWithEstimates(normalizedDPE as any);
  console.log('[DEBUG enrichedDPE]', {
  living_area: enrichedDPE.living_area,
  besoin_chauffage: enrichedDPE.besoin_chauffage,
  besoin_ecs: enrichedDPE.besoin_ecs,
  deperditions_murs: enrichedDPE.deperditions_murs,
  has_estimated_data: enrichedDPE.has_estimated_data,
});
  
  const { data: existingImprovements } = await supabase
    .from('scenario_improvements')
    .select('id')
    .eq('scenario_id', scenarioId);

  if (existingImprovements && existingImprovements.length > 0) {
    const { error: deleteError } = await supabase
      .from('scenario_improvements')
      .delete()
      .eq('scenario_id', scenarioId);

    if (deleteError) {
      throw new Error('Failed to clear existing improvements');
    }
  }

  const improvementsToInsert = optimizedScenario.improvements.map((improvement, index) => {
    const improvementData: any = {
      scenario_id: scenarioId,
      ...improvement.calculation,
      priority: index + 1,
    };

    if (improvement.calculation.insulation_coverage_percentage !== undefined) {
      improvementData.insulation_coverage_percentage = improvement.calculation.insulation_coverage_percentage;
    }

    if (improvement.calculation.insulation_coverage_notes !== undefined) {
      improvementData.insulation_coverage_notes = improvement.calculation.insulation_coverage_notes;
    }

    const { energy_type, cop, is_gas_to_electric_replacement, ...dataWithoutExtraFields } = improvementData;

    if (energy_type !== undefined || cop !== undefined || is_gas_to_electric_replacement !== undefined) {
      dataWithoutExtraFields.energy_savings_breakdown = {
        ...(dataWithoutExtraFields.energy_savings_breakdown || {}),
        ...(energy_type !== undefined && { energy_type }),
        ...(cop !== undefined && { cop }),
        ...(is_gas_to_electric_replacement !== undefined && { is_gas_to_electric_replacement }),
      };
    }

    return dataWithoutExtraFields;
  });

  const { error: insertError } = await supabase
    .from('scenario_improvements')
    .insert(improvementsToInsert);

  if (insertError) {
    throw new Error(`Failed to insert optimized improvements: ${insertError.message}`);
  }

  const { error: updateError } = await supabase
    .from('work_scenarios')
    .update({
      total_energy_savings: optimizedScenario.energySavings,
      total_cost_savings: optimizedScenario.totalAnnualSavings,
      total_investment: optimizedScenario.totalInvestment,
      payback_period_years: optimizedScenario.paybackPeriod,
      current_energy_class: optimizedScenario.currentLabel,
      current_climate_class: optimizedScenario.currentGESLabel,
      projected_energy_class: optimizedScenario.projectedLabel,
      projected_climate_class: optimizedScenario.projectedGESLabel,
      projected_energy_consumption: optimizedScenario.projectedEPperM2 ?? null,
      current_energy_consumption: optimizedScenario.currentEPperM2 ?? null,
      projected_ges_emissions: optimizedScenario.projectedGESperM2 ?? null,
      current_ges_emissions: optimizedScenario.currentGESperM2 ?? null,
      optimization_strategy: optimizedScenario.strategy,
      is_auto_generated: true,
    })
    .eq('id', scenarioId);

  if (updateError) {
    throw new Error(`Failed to update scenario: ${updateError.message}`);
  }

  console.log('Successfully applied optimization to scenario:', {
    scenarioId,
    improvementsCount: improvementsToInsert.length,
    strategy: optimizedScenario.strategy,
  });
}

export async function createAndApplyOptimizedScenario(
  dpeId: string,
  strategy: OptimizationStrategy,
  onProgress?: ProgressCallback
): Promise<string> {
  // 1. Get authenticated user
  const { data: userData, error: userError } = await supabase.auth.getUser();

  if (userError || !userData?.user) {
    throw new Error('Utilisateur non authentifié');
  }

  // 2. Récupérer les données DPE pour calculer les classes actuelles
  const { data: dpeData, error: dpeError } = await supabase
    .from('dpe_data')
    .select('*')
    .eq('id', dpeId)
    .maybeSingle();

  if (dpeError || !dpeData) {
    throw new Error('DPE introuvable');
  }

  // 3. Calculer les classes énergétiques et climatiques actuelles avec les facteurs 2026
  let currentConsumption2026: number;

  const hasDetailedData = dpeData.conso_chauffage_ef && dpeData.conso_ecs_ef;

  if (hasDetailedData) {
    // Calcul différencié avec données détaillées
    const consoElecEF = (dpeData.heating_energy === 'electricite' ? (dpeData.conso_chauffage_ef || 0) : 0) +
                        (dpeData.hot_water_energy === 'electricite' ? (dpeData.conso_ecs_ef || 0) : 0) +
                        (dpeData.conso_eclairage_ef || 0) +
                        (dpeData.conso_auxiliaires_ef || 0);

    const consoNonElecEF = ((dpeData.heating_energy !== 'electricite' ? (dpeData.conso_chauffage_ef || 0) : 0) +
                            (dpeData.hot_water_energy !== 'electricite' ? (dpeData.conso_ecs_ef || 0) : 0));

    const epElec2026 = consoElecEF * 1.9;
    const epNonElec = consoNonElecEF * 1.0;
    const epTotal2026 = epElec2026 + epNonElec;

    currentConsumption2026 = Math.round(epTotal2026 / dpeData.living_area);
  } else {
    // Calcul simplifié sans données détaillées
    const hasElectricity = dpeData.heating_energy === 'electricite' || dpeData.hot_water_energy === 'electricite';

    if (hasElectricity && dpeData.primary_energy_consumption) {
      currentConsumption2026 = Math.round(dpeData.primary_energy_consumption * (1.9 / 2.3));
    } else {
      currentConsumption2026 = dpeData.primary_energy_consumption || 0;
    }
  }

  // Calculer la classe énergétique actuelle
  const { projectedLabel: currentEnergyClass } = calculateProjectedDPELabel(
    currentConsumption2026,
    0,
    dpeData.living_area
  );

  // Calculer les émissions GES actuelles avec les facteurs 2026
  const gesCalculation = calculateTotalGESWithImprovements(
    dpeData,
    [],
    dpeData.living_area
  );

  const currentGESEmissions = gesCalculation.currentTotal;
  const currentClimateClass = gesCalculation.projectedLabel;

  // 4. Generate the optimization
  const optimizedScenario = await generateOptimizationForDPE(dpeId, strategy, onProgress);

  // 5. Create a new scenario with automatic naming and current classes
  const scenarioName = strategy === 'best_label'
    ? 'Meilleure lettre'
    : 'Meilleure rentabilité';

  const { data: newScenario, error } = await supabase
    .from('work_scenarios')
    .insert({
      user_id: userData.user.id,
      dpe_id: dpeId,
      name: scenarioName,
      current_energy_consumption: currentConsumption2026,
      current_energy_class: currentEnergyClass,
      current_ges_emissions: currentGESEmissions,
      current_climate_class: currentClimateClass,
      optimization_strategy: strategy,
      is_auto_generated: true
    })
    .select()
    .single();

  if (error || !newScenario) {
    throw new Error('Impossible de créer le scénario');
  }

  // 6. Apply the optimization to the new scenario
  await applyOptimizationToScenario(newScenario.id, optimizedScenario);

  return newScenario.id;
}
